import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Skeleton } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import PerfectScrollbar from 'react-perfect-scrollbar';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { replaceUnderscoreWithTo } from '../../common/functions/replace_underscore_with_To.function';
import { convertDateToLocalString } from '../../common/functions/convert_date_to_local_string.function';
import {
  isNewFttsImplementation,
  getAlarmTimestamp,
  getFirstOccurredTimestamp,
  getLastOccurredTimestamp,
} from '../../common/functions/ftts_new_implementation.function';
import {
  isNewIpranImplementation,
  getAlarmTimestamp as getAlarmTimestampIpran,
  getFirstOccurredTimestamp as getFirstOccurredTimestampIpran,
  getLastOccurredTimestamp as getLastOccurredTimestampIpran,
} from '../../common/functions/ipran_new_implementation.function';
import {
  isNewOtnImplementation,
  isOtnEndpointAlarm,
  getAlarmTimestamp as getAlarmTimestampOtn,
  getFirstOccurredTimestamp as getFirstOccurredTimestampOtn,
  getLastOccurredTimestamp as getLastOccurredTimestampOtn,
} from '../../common/functions/otn_new_implementation.function';
import { OtnLinkAlarmCard } from './otnEndpointUi';
import './otnEndpointStyles.css';

// Utility function to calculate and format duration
const calculateDuration = (alarmActiveTime, alarmInactiveTime) => {
  if (!alarmActiveTime || !alarmInactiveTime) {
    return 'N/A';
  }

  try {
    const activeTime = new Date(alarmActiveTime);
    const inactiveTime = new Date(alarmInactiveTime);

    if (isNaN(activeTime.getTime()) || isNaN(inactiveTime.getTime())) {
      return 'N/A';
    }

    const diffMs = inactiveTime.getTime() - activeTime.getTime();

    if (diffMs < 0) {
      return 'N/A';
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return '0 mins';
    }

    return parts.join(' ');
  } catch (error) {
    return 'N/A';
  }
};

// Utility function to calculate alarm active time for live alarms
const calculateAlarmActiveTime = (alarmActiveTime) => {
  if (!alarmActiveTime) {
    return 'N/A';
  }

  try {
    const startTime = new Date(alarmActiveTime);
    const currentTime = new Date();

    if (isNaN(startTime.getTime())) {
      return 'N/A';
    }

    const diffMs = currentTime.getTime() - startTime.getTime();

    if (diffMs < 0) {
      return 'N/A';
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return '0 mins';
    }

    return parts.join(' ');
  } catch (error) {
    return 'N/A';
  }
};

const regionColorsText = {
  North: '#415F8C',
  'Central A': '#825144',
  'Central B': '#6E7A2F',
  South: '#937535',
};

const regionColorsbackground = {
  North: '#E8F1FD',
  'Central A': '#F9E5DF',
  'Central B': '#E9F3B6',
  South: '#FDEECE',
};

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  // borderRadius: theme.shape.borderRadius,
  border: '1px solid #E8E9E9',
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  borderRadius: '8px',
  boxShadow: '0 0 1px 0 #1a1f4360',
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    // width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  height: '44px',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

export default function ActiveAlarmsCard({
  data,
  loading,
  setLoadingApi,
  tabCheck,
  loadingApi,
  value,
  alarmType,
  handleReset,
  handleApplyChanges,
  setApplyFilters,
  setTotalAlarms,
  totalAlarms,
  setSelectedAlarm,
  setFiltersValue,
  filtersValue,
  setRing,
  setCity,
  setRegion,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [expandedAlarms, setExpandedAlarms] = useState({});
  const stb = sessionStorage.getItem('selectedTab');
  const [dataArr, setDataArr] = useState([
    { name: 'North', count: 0, background: '#AFCEF9', color: '#000' },
    { name: 'Central A', count: 0, background: '#E39B88', color: '#000' },
    { name: 'Central B', count: 0, background: '#CBDD6A', color: '#000' },
    { name: 'South', count: 0, background: '#FFD886', color: '#000' },
  ]);

  useEffect(() => {
    if (data) {
      // For MPBN, group alarms by trunk_name
      if (sessionStorage.getItem('selectedTab') === 'mpbn') {
        const groupedByTrunk = data.reduce((acc, alarm) => {
          const trunkName = alarm?.trunk_name || 'Unknown Trunk';
          if (!acc[trunkName]) {
            acc[trunkName] = {
              trunk_name: alarm?.trunk_name,
              trunk_region: alarm?.trunk_region,
              trunk_city: alarm?.trunk_city,
              length: alarm?.length,
              region: alarm?.region || alarm?.trunk_region,
              alarms: [],
            };
          }
          acc[trunkName].alarms.push(alarm);
          return acc;
        }, {});

        // Convert grouped object to array
        const groupedArray = Object.values(groupedByTrunk);
        setFilteredData(groupedArray);
      } else {
        setFilteredData(data);
      }

      // Always update region counts when data changes (for live alarms from socket)
      if (!filtersValue.region) {
        const currentTab = sessionStorage.getItem('selectedTab');

        CountForRegions(
          currentTab === 'mpbn'
            ? Object.values(
                data.reduce((acc, alarm) => {
                  const trunkName = alarm?.trunk_name || 'Unknown Trunk';
                  if (!acc[trunkName]) {
                    acc[trunkName] = {
                      trunk_name: alarm?.trunk_name,
                      trunk_region: alarm?.trunk_region,
                      trunk_city: alarm?.trunk_city,
                      length: alarm?.length,
                      region: alarm?.region || alarm?.trunk_region,
                      alarms: [],
                    };
                  }
                  acc[trunkName].alarms.push(alarm);
                  return acc;
                }, {})
              )
            : data
        );
      }
    }
  }, [data, alarmType]);
  useEffect(() => {
    return () => {
      setFilters('');
      setDataArr([
        { name: 'North', count: 0, background: '#AFCEF9', color: '#000' },
        { name: 'Central A', count: 0, background: '#E39B88', color: '#000' },
        { name: 'Central B', count: 0, background: '#CBDD6A', color: '#000' },
        { name: 'South', count: 0, background: '#FFD886', color: '#000' },
      ]);
    };
  }, [alarmType]);
  useEffect(() => {
    if (!filtersValue.region && filteredData) {
      CountForRegions(filteredData);
    }
  }, [alarmType, filteredData, data]);

  const CountForRegions = (dataToCount = filteredData) => {
    const currentTab = sessionStorage.getItem('selectedTab');

    if (data?.length === 0 || alarmType === 'History') {
      setDataArr([
        { name: 'North', count: 0, background: '#AFCEF9', color: '#000' },
        { name: 'Central A', count: 0, background: '#E39B88', color: '#000' },
        { name: 'Central B', count: 0, background: '#CBDD6A', color: '#000' },
        { name: 'South', count: 0, background: '#FFD886', color: '#000' },
      ]);
    } else {
      const updatedDataArr = [
        { name: 'North', count: 0, background: '#AFCEF9', color: '#000' },
        { name: 'Central A', count: 0, background: '#E39B88', color: '#000' },
        { name: 'Central B', count: 0, background: '#CBDD6A', color: '#000' },
        { name: 'South', count: 0, background: '#FFD886', color: '#000' },
      ].map((region) => {
        let count = 0;

        if (currentTab === 'mpbn') {
          // For MPBN grouped data, count all alarms within each trunk
          dataToCount?.forEach((trunk) => {
            if (trunk?.alarms && Array.isArray(trunk.alarms)) {
              // Count alarms that match this region
              const regionAlarms = trunk.alarms.filter(
                (alarm) => (alarm.trunk_region || alarm.region) === region.name
              );
              count += regionAlarms.length;
            }
          });
        } else if (currentTab === 'ipran') {
          count =
            dataToCount?.filter((alarm) => (alarm.source_region || alarm.region) === region.name)
              ?.length || 0;
        } else {
          // For OTN and FTTS networks
          count = dataToCount?.filter((alarm) => alarm.region === region.name)?.length || 0;
        }

        return { ...region, count };
      });

      setDataArr(updatedDataArr);
    }
  };

  useEffect(() => {
    if (data && searchQuery) {
      // For MPBN with grouping
      if (sessionStorage.getItem('selectedTab') === 'mpbn') {
        const groupedByTrunk = data.reduce((acc, alarm) => {
          const trunkName = alarm?.trunk_name || 'Unknown Trunk';
          if (!acc[trunkName]) {
            acc[trunkName] = {
              trunk_name: alarm?.trunk_name,
              trunk_region: alarm?.trunk_region,
              trunk_city: alarm?.trunk_city,
              length: alarm?.length,
              region: alarm?.region || alarm?.trunk_region,
              alarms: [],
            };
          }
          acc[trunkName].alarms.push(alarm);
          return acc;
        }, {});

        // Filter based on search query
        const filtered = Object.values(groupedByTrunk).filter(
          (trunk) =>
            trunk?.trunk_name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            trunk?.trunk_region?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            trunk?.trunk_city?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            trunk?.alarms?.some(
              (alarm) =>
                alarm?.layer_name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
                alarm?.alarm_mapped_id?.toLowerCase().includes(searchQuery?.toLowerCase())
            )
        );
        setFilteredData(filtered);
      } else {
        const filtered = data?.filter(
          (alarm) =>
            alarm?.layer_name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            alarm?.region?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            alarm?.city?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            alarm?.source_city?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            alarm?.ring_name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            alarm?.end_a_alarm_id?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
            alarm?.end_b_alarm_id?.toLowerCase().includes(searchQuery?.toLowerCase())
        );
        setFilteredData(filtered); // Set filtered data to filteredData state
      }
    } else {
      // If there's no search query, reset filtered data to the original data
      if (sessionStorage.getItem('selectedTab') === 'mpbn' && data) {
        const groupedByTrunk = data.reduce((acc, alarm) => {
          const trunkName = alarm?.trunk_name || 'Unknown Trunk';
          if (!acc[trunkName]) {
            acc[trunkName] = {
              trunk_name: alarm?.trunk_name,
              trunk_region: alarm?.trunk_region,
              trunk_city: alarm?.trunk_city,
              length: alarm?.length,
              region: alarm?.region || alarm?.trunk_region,
              alarms: [],
            };
          }
          acc[trunkName].alarms.push(alarm);
          return acc;
        }, {});
        setFilteredData(Object.values(groupedByTrunk));
      } else {
        setFilteredData(data);
      }
    }
  }, [data, searchQuery]);

  const setFilters = (region) => {
    if (region && filtersValue?.region === region) {
      handleReset();
      setFiltersValue({
        region: '',
        city: '',
        ring: '',
        site: '',
        link: '',
      });
      // setFiltersValue({
      //   region: '',
      //   city: '',
      //   ring: '',
      // })
      // setRegion('')
      // setCity('')
      // setRing('')
    } else {
      setFiltersValue({ region });
      if (region) {
        // setApplyFilters(true);
      }
      handleApplyChanges(region);
    }
  };

  const toggleAlarmDetails = (index) => {
    setExpandedAlarms((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      <Search sx={{ width: '328px' }}>
        <SearchIconWrapper>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M9.58341 2.29169C5.55634 2.29169 2.29175 5.55628 2.29175 9.58335C2.29175 13.6104 5.55634 16.875 9.58341 16.875C13.6105 16.875 16.8751 13.6104 16.8751 9.58335C16.8751 5.55628 13.6105 2.29169 9.58341 2.29169ZM1.04175 9.58335C1.04175 4.86592 4.86598 1.04169 9.58341 1.04169C14.3008 1.04169 18.1251 4.86592 18.1251 9.58335C18.1251 14.3008 14.3008 18.125 9.58341 18.125C4.86598 18.125 1.04175 14.3008 1.04175 9.58335ZM16.2248 16.2247C16.4689 15.9807 16.8646 15.9807 17.1087 16.2247L18.7754 17.8914C19.0194 18.1355 19.0194 18.5312 18.7754 18.7753C18.5313 19.0194 18.1356 19.0194 17.8915 18.7753L16.2248 17.1086C15.9807 16.8646 15.9807 16.4688 16.2248 16.2247Z"
              fill="#999FA7"
            />
          </svg>
        </SearchIconWrapper>

        <StyledInputBase
          placeholder="Search…"
          inputProps={{ 'aria-label': 'search' }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Search>
      {sessionStorage.getItem('selectedTab') !== 'mpbn' && (
        <div className="active_alarms_main">
          {dataArr.map((item, index) => (
            <div
              key={index}
              className="active_alarms_card_layout"
              onClick={loadingApi ? () => setFilters(item?.name) : undefined}
              style={{
                background:
                  filtersValue?.region === ''
                    ? item?.background
                    : filtersValue?.region === item?.name
                      ? item?.background
                      : '#e6e6e6',
                pointerEvents: !loadingApi ? 'none' : 'auto', // Disable clicks while loading
                opacity: !loadingApi ? 0.5 : 1, // Indicate loading state
                textAlign: 'center',
              }}
            >
              <h3>{item?.name}</h3>
              <span>{item?.count}</span>
            </div>
          ))}
        </div>
      )}

      <div
        className="alerts_list_main"
        style={
          sessionStorage.getItem('selectedTab') === 'mpbn' ? { height: 'calc(100vh - 216px)' } : {}
        }
      >
        <PerfectScrollbar>
          {filteredData?.length === 0 ? (
            <p>No Alarms To Show</p>
          ) : loading ? (
            [1, 2, 3, 4].map((_, index) => (
              <div className="toast-item error" key={index}>
                <div className="toast error">
                  <Skeleton variant="text" width={200} height={30} />
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <Skeleton variant="text" width={100} height={20} />
                    <Skeleton variant="text" width={100} height={20} />
                  </div>
                  <Skeleton variant="text" width={150} height={20} />
                </div>
              </div>
            ))
          ) : (
            filteredData &&
            filteredData?.map((alarm, index) => {
              const isOtnEndpointCard =
                sessionStorage.getItem('selectedTab') === 'otn' &&
                isNewOtnImplementation() &&
                isOtnEndpointAlarm(alarm);

              return isOtnEndpointCard ? (
                <div className="otn-alarm-item" key={index} onClick={() => setSelectedAlarm(alarm)}>
                  <OtnLinkAlarmCard alarm={alarm} alarmType={alarmType} />
                </div>
              ) : (
                <div
                  className="single_alert_card"
                  key={index}
                  onClick={() => {
                    if (
                      sessionStorage.getItem('selectedTab') === 'mpbn' &&
                      alarm?.alarms &&
                      alarm.alarms.length > 0
                    ) {
                      setSelectedAlarm(alarm.alarms[0]);
                    } else {
                      setSelectedAlarm(alarm);
                    }
                  }}
                >
                  <div className="top_row">
                    {sessionStorage.getItem('selectedTab') !== 'mpbn' ? (
                      <>
                        <p className="date_time">
                          {(() => {
                            const timestamp =
                              sessionStorage.getItem('selectedTab') === 'ipran'
                                ? getAlarmTimestampIpran(alarm)
                                : sessionStorage.getItem('selectedTab') === 'otn'
                                  ? getAlarmTimestampOtn(alarm)
                                  : getAlarmTimestamp(alarm);

                            return timestamp ? convertDateToLocalString(timestamp) : '--';
                          })()}
                        </p>
                        <div className="top_row_left">
                          <p className="city_box">
                            {alarm?.city ? alarm.city : alarm?.source_city || alarm?.trunk_city}
                          </p>
                          <p
                            className="region_box"
                            style={{
                              color:
                                regionColorsText[
                                  alarm?.region || alarm?.source_region || alarm?.trunk_region
                                ],
                              background:
                                regionColorsbackground[
                                  alarm?.region || alarm?.source_region || alarm?.trunk_region
                                ],
                            }}
                          >
                            {alarm?.region || alarm?.source_region || alarm?.trunk_region}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p
                          className="region_box"
                          style={{
                            color:
                              regionColorsText[
                                alarm?.trunk_region ||
                                  alarm?.region ||
                                  alarm?.alarms?.[0]?.trunk_region
                              ],
                            background:
                              regionColorsbackground[
                                alarm?.trunk_region ||
                                  alarm?.region ||
                                  alarm?.alarms?.[0]?.trunk_region
                              ],
                          }}
                        >
                          {alarm?.trunk_region || alarm?.region || alarm?.alarms?.[0]?.trunk_region}
                        </p>
                        <p className="city_box">
                          {alarm?.trunk_city || alarm?.city || alarm?.alarms?.[0]?.trunk_city}
                        </p>
                      </>
                    )}
                  </div>

                  {/* MPBN: Show trunk details with expandable alarm details */}
                  {sessionStorage.getItem('selectedTab') === 'mpbn' ? (
                    <>
                      {/* Trunk Name with Down Arrow - Clickable */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          marginTop: '14px',
                          cursor: 'pointer',
                          gap: '8px',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAlarmDetails(index);
                        }}
                      >
                        <h3
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#090C0D',
                            marginBottom: '0px',
                            lineHeight: '1.4',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            flex: 1,
                          }}
                          title={alarm?.trunk_name}
                        >
                          {alarm?.trunk_name || '--'}
                        </h3>
                        <KeyboardArrowDownIcon
                          style={{
                            fontSize: '20px',
                            color: '#495057',
                            transform: expandedAlarms[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            flexShrink: 0,
                            marginTop: '-2px',
                          }}
                        />
                      </div>

                      {/* Length and Alarm Count Info */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: '10px',
                          gap: '12px',
                        }}
                      >
                        {alarm?.alarms && alarm.alarms.length > 0 && (
                          <span className="alerts_info" style={{ marginTop: '0px' }}>
                            <p style={{ color: '#090C0D' }}>Alarms:</p>
                            <p style={{ fontWeight: '500' }}>{alarm.alarms.length}</p>
                          </span>
                        )}
                        {alarm?.length && (
                          <span className="alerts_info" style={{ marginTop: '0px' }}>
                            <p style={{ color: '#090C0D' }}>Length:</p>
                            <p style={{ fontWeight: '500' }}>
                              {parseFloat(alarm?.length).toFixed(2)} km
                            </p>
                          </span>
                        )}
                      </div>

                      {/* Collapsible Alarm Details Section */}
                      {expandedAlarms[index] && (
                        <div>
                          {/* Gray Border Divider */}
                          <div
                            style={{
                              borderTop: '1px solid #dee2e6',
                              marginTop: '12px',
                              marginBottom: '12px',
                            }}
                          />

                          {/* Multiple Alarms for this Trunk */}
                          {alarm?.alarms && alarm.alarms.length > 0 ? (
                            alarm.alarms.map((singleAlarm, alarmIdx) => (
                              <div
                                key={alarmIdx}
                                style={{
                                  cursor: 'pointer',
                                  padding: '8px',
                                  marginLeft: '-8px',
                                  marginRight: '-8px',
                                  borderRadius: '6px',
                                  transition: 'background-color 0.2s ease',
                                  marginBottom: alarmIdx < alarm.alarms.length - 1 ? '16px' : '0',
                                  paddingBottom:
                                    alarmIdx < alarm.alarms.length - 1 ? '16px' : '8px',
                                  borderBottom:
                                    alarmIdx < alarm.alarms.length - 1
                                      ? '1px solid #e9ecef'
                                      : 'none',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAlarm(singleAlarm);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <p
                                  style={{
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    color: '#BD1D23',
                                    fontWeight: '600',
                                    fontSize: '13px',
                                    lineHeight: '1.4',
                                    marginBottom: '10px',
                                  }}
                                  title={singleAlarm?.layer_name}
                                >
                                  {replaceUnderscoreWithTo(singleAlarm?.layer_name)}
                                </p>
                                <span className="alerts_info">
                                  <p style={{ color: '#090C0D' }}>Last Occurred:</p>
                                  <p>{convertDateToLocalString(getAlarmTimestamp(singleAlarm))}</p>
                                </span>
                                <span className="alerts_info">
                                  <p style={{ color: '#090C0D' }}>Alarm ID:</p>
                                  <p>{singleAlarm?.alarm_mapped_id || '--'}</p>
                                </span>
                                <span className="alerts_info">
                                  <p style={{ color: '#090C0D' }}>Alarm Source:</p>
                                  <p>{singleAlarm?.alarm_source || '--'}</p>
                                </span>
                                <span className="alerts_info">
                                  <p style={{ color: '#090C0D' }}>Site Code:</p>
                                  <p>{singleAlarm?.source_site_code || '--'}</p>
                                </span>
                              </div>
                            ))
                          ) : (
                            <div>
                              <p
                                style={{
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  color: '#BD1D23',
                                  fontWeight: '600',
                                  fontSize: '13px',
                                  lineHeight: '1.4',
                                  marginBottom: '10px',
                                }}
                                title={alarm?.layer_name}
                              >
                                {replaceUnderscoreWithTo(alarm?.layer_name)}
                              </p>
                              <span className="alerts_info">
                                <p style={{ color: '#090C0D' }}>Last Occurred:</p>
                                <p>
                                  {convertDateToLocalString(
                                    sessionStorage.getItem('selectedTab') === 'ipran'
                                      ? getAlarmTimestampIpran(alarm)
                                      : sessionStorage.getItem('selectedTab') === 'otn'
                                        ? getAlarmTimestampOtn(alarm)
                                        : getAlarmTimestamp(alarm)
                                  )}
                                </p>
                              </span>
                              <span className="alerts_info">
                                <p style={{ color: '#090C0D' }}>Alarm ID:</p>
                                <p>{alarm?.alarm_mapped_id || '--'}</p>
                              </span>
                              <span className="alerts_info">
                                <p style={{ color: '#090C0D' }}>Alarm Source:</p>
                                <p>{alarm?.alarm_source || '--'}</p>
                              </span>
                              <span className="alerts_info">
                                <p style={{ color: '#090C0D' }}>Site Code:</p>
                                <p>{alarm?.source_site_code || '--'}</p>
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Non-MPBN: Show regular alarm details */
                    <>
                      <h3>
                        {sessionStorage.getItem('selectedTab') === 'otn' ||
                        sessionStorage.getItem('selectedTab') === 'ipran'
                          ? alarm?.layer_name
                          : replaceUnderscoreWithTo(alarm?.layer_name)}
                      </h3>
                      {sessionStorage.getItem('selectedTab') === 'ftts' && (
                        <span className="alerts_info">
                          <p style={{ color: '#090C0D' }}>Ring:</p>
                          <p>{alarm?.ring_name}</p>
                        </span>
                      )}
                      <span className="alerts_info">
                        <p style={{ color: '#090C0D' }}>Commission Date:</p>
                        <p>
                          {alarm?.commissioned_at
                            ? new Date(alarm.commissioned_at).toLocaleDateString('en-GB')
                            : '--'}
                        </p>
                      </span>
                      {((sessionStorage.getItem('selectedTab') === 'ftts' &&
                        isNewFttsImplementation()) ||
                        (sessionStorage.getItem('selectedTab') === 'ipran' &&
                          isNewIpranImplementation()) ||
                        (sessionStorage.getItem('selectedTab') === 'otn' &&
                          isNewOtnImplementation())) &&
                        alarmType === 'history' && (
                          <span className="alerts_info">
                            <p style={{ color: '#090C0D' }}>Alarm Duration:</p>
                            <p>
                              {calculateDuration(
                                sessionStorage.getItem('selectedTab') === 'ipran'
                                  ? getFirstOccurredTimestampIpran(alarm)
                                  : sessionStorage.getItem('selectedTab') === 'otn'
                                    ? getFirstOccurredTimestampOtn(alarm)
                                    : getFirstOccurredTimestamp(alarm),
                                sessionStorage.getItem('selectedTab') === 'ipran'
                                  ? getLastOccurredTimestampIpran(alarm)
                                  : sessionStorage.getItem('selectedTab') === 'otn'
                                    ? getLastOccurredTimestampOtn(alarm)
                                    : getLastOccurredTimestamp(alarm)
                              )}
                            </p>
                          </span>
                        )}
                      {((sessionStorage.getItem('selectedTab') === 'ftts' &&
                        isNewFttsImplementation()) ||
                        (sessionStorage.getItem('selectedTab') === 'ipran' &&
                          isNewIpranImplementation()) ||
                        (sessionStorage.getItem('selectedTab') === 'otn' &&
                          isNewOtnImplementation() &&
                          !isOtnEndpointAlarm(alarm))) &&
                        alarmType === 'live' && (
                          <span className="alerts_info">
                            <p style={{ color: '#090C0D' }}>Alarm Lifetime:</p>
                            <p>
                              {calculateAlarmActiveTime(
                                sessionStorage.getItem('selectedTab') === 'ipran'
                                  ? getFirstOccurredTimestampIpran(alarm)
                                  : sessionStorage.getItem('selectedTab') === 'otn'
                                    ? getFirstOccurredTimestampOtn(alarm)
                                    : getFirstOccurredTimestamp(alarm)
                              )}
                            </p>
                          </span>
                        )}
                    </>
                  )}
                </div>
              );
            })
          )}
        </PerfectScrollbar>
      </div>
    </>
  );
}
